/**
 * Document Upload Section
 * Reusable component for managing documents on investor/contract pages
 * Used by internal staff - documents are auto-approved on upload
 */

import React, { useState, useEffect, useCallback } from 'react';
import useAsyncData from '../../hooks/useAsyncData';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { apiGet, apiUpload, apiDelete } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDate, formatFileSize } from '../../utils/formatters';
import useDocumentActions from '../../hooks/useDocumentActions';
import DocumentPreviewDialog from './DocumentPreviewDialog';
import FileDropZone from '../common/FileDropZone';

// Fallback document types (used if API fails to load)
const FALLBACK_DOCUMENT_TYPES = [
  { code: 'kyc', display_name: 'KYC Document' },
  { code: 'proof_of_payment', display_name: 'Proof of Payment' },
  { code: 'bank_statement', display_name: 'Bank Statement' },
  { code: 'contract', display_name: 'Contract Document' },
  { code: 'supporting_doc', display_name: 'Supporting Document' },
  { code: 'other', display_name: 'Other' },
];

const DocumentUploadSection = ({
  investorId,
  contractId,
  title = 'Documents',
  collapsible = true,
  defaultExpanded = true,
  onUploadComplete,
}) => {
  const notify = useNotification();
  const [documentTypes, setDocumentTypes] = useState(FALLBACK_DOCUMENT_TYPES);
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Document preview & download (shared hook)
  const docActions = useDocumentActions();

  // Upload dialog state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    document_type: '',
    notes: '',
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load document types from API (silent fallback, no loading/error state needed)
  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await apiGet('/refdata/document-types/active');
      if (response.data && response.data.length > 0) {
        setDocumentTypes(response.data);
      }
    } catch (err) {
      logger.warn('Failed to load document types, using fallback', { message: err.message });
      // Keep using fallback types
    }
  }, []);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  const {
    data: documents,
    loading,
    reload: loadDocuments,
  } = useAsyncData(
    async () => {
      if (!investorId && !contractId) return [];
      const endpoint = contractId
        ? `/admin/documents/by-contract/${contractId}`
        : `/admin/documents/by-investor/${investorId}`;
      const response = await apiGet(endpoint);
      return response.data || [];
    },
    { defaultValue: [], fetchOnMount: !!(investorId || contractId), deps: [investorId, contractId], extractData: false, onError: notify.error }
  );

  const handleUpload = async () => {
    if (!uploadData.document_type || !uploadData.file) {
      notify.error('Please select a document type and file');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('document_type', uploadData.document_type);
      if (investorId) formData.append('investor_id', investorId);
      if (contractId) formData.append('contract_id', contractId);
      if (uploadData.notes) formData.append('notes', uploadData.notes);

      await apiUpload('/admin/documents/upload', formData);

      notify.success('Document uploaded successfully');
      setUploadDialog(false);
      setUploadData({ document_type: '', notes: '', file: null });
      loadDocuments();

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (err) {
      notify.error('Failed to upload document: ' + (err.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (doc) => {
    setDeleteTarget(doc);
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await apiDelete(`/admin/documents/${deleteTarget.id}`);
      notify.success('Document deleted successfully');
      setDeleteDialog(false);
      setDeleteTarget(null);
      loadDocuments();
    } catch (err) {
      notify.error('Failed to delete document: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'reupload_requested': return 'info';
      default: return 'default';
    }
  };

  const getDocumentTypeLabel = (code) => {
    const found = documentTypes.find(t => t.code === code);
    return found?.display_name || code;
  };

  const renderHeader = () => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {collapsible && (
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
        <Typography variant="h6">{title}</Typography>
        <Chip label={documents.length} size="small" color="primary" sx={{ ml: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Refresh">
          <IconButton onClick={loadDocuments} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={() => setUploadDialog(true)}
        >
          Upload
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (documents.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No documents uploaded yet</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialog(true)}
            sx={{ mt: 2 }}
          >
            Upload Document
          </Button>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {doc.file_name}
                  </Typography>
                  {doc.notes && (
                    <Typography variant="caption" color="text.secondary">
                      {doc.notes}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{getDocumentTypeLabel(doc.document_type)}</TableCell>
                <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(doc.created_at)}</Typography>
                  {doc.uploaded_by_email && (
                    <Typography variant="caption" color="text.secondary">
                      {doc.uploaded_by_email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={doc.status}
                    size="small"
                    color={getStatusColor(doc.status)}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Preview">
                    <IconButton size="small" onClick={() => docActions.openPreview(doc.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => docActions.downloadDocument(doc.id)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteClick(doc)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      {renderHeader()}


      <Collapse in={expanded}>
        {renderContent()}
      </Collapse>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={uploadData.document_type}
                label="Document Type"
                onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.code} value={type.code}>
                    {type.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              placeholder="Add any relevant notes about this document"
            />

            <FileDropZone
              file={uploadData.file}
              onFile={(f) => setUploadData({ ...uploadData, file: f })}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              helperText="PDF, JPG, PNG, DOC, DOCX, XLS, XLSX (max 10MB)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadData.file || !uploadData.document_type}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.file_name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog {...docActions} />
    </Paper>
  );
};

export default DocumentUploadSection;
